import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Database, Eye, Layers, Loader2, Plus } from "lucide-react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogPrimaryButton,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getGameBankLotteries, GameBankLottery } from "@/Api/gameBank";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";
import { cn } from "@/lib/utils";
import { pad2 } from "./shared";
import { GameBankSheetsPanel } from "./Sheets";

/**
 * Banco de Bolões > Banco de Jogos (LOT-62).
 * Uma aba por loteria; dentro dela os jogos ficam agrupados por quantidade de dezenas, e abaixo ficam as
 * planilhas de cadastro (Em andamento → Finalizado, ou Arquivado; conferência Pendente → Analisado).
 */
export const PageList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isAdmin } = usePermissions();
  const canManage = isAdmin() || hasPermission(PERMISSIONS.MANAGE_GAME_BANK);

  const [lotteries, setLotteries] = useState<GameBankLottery[]>([]);
  const [loading, setLoading] = useState(true);
  const [dezenasFilter, setDezenasFilter] = useState<string>("all");
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerDezenas, setRegisterDezenas] = useState("");
  // Aba sob o mouse: o hover usa a cor da loteria (não dá para fazer com classe, a cor vem da API)
  const [hoveredLotteryId, setHoveredLotteryId] = useState<number | null>(null);

  const loadLotteries = useCallback(() => {
    getGameBankLotteries()
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data?.response)) setLotteries(data.response);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadLotteries();
  }, [loadLotteries]);

  const selected = useMemo(() => {
    const fromUrl = Number(searchParams.get("loteria"));
    return lotteries.find((lottery) => lottery.id === fromUrl) ?? lotteries[0] ?? null;
  }, [lotteries, searchParams]);

  const groups = useMemo(() => {
    if (!selected) return [];
    return dezenasFilter === "all"
      ? selected.groups
      : selected.groups.filter((group) => String(group.dezenas) === dezenasFilter);
  }, [selected, dezenasFilter]);

  const selectLottery = (id: number) => {
    setDezenasFilter("all");
    setSearchParams({ loteria: String(id) });
  };

  const openRegister = () => {
    // Super Sete tem quantidade fixa de números por jogo
    setRegisterDezenas(selected?.rules.length ? String(selected.rules.length) : "");
    setRegisterOpen(true);
  };

  const confirmRegister = () => {
    if (!selected) return;
    const dezenas = Number(registerDezenas);
    const max = selected.rules.length ?? selected.rules.max - selected.rules.min + 1;
    if (!dezenas || dezenas < 1 || dezenas > max) return;
    setRegisterOpen(false);
    navigate(`/banco-de-boloes/${selected.id}/planilhas/nova?dezenas=${dezenas}`);
  };

  return (
    <Main>
      <PageHeader
        icon={Database}
        title="Banco de Jogos"
        description="Jogos organizados por quantidade de dezenas e as planilhas de cadastro."
      >
        {canManage && selected && (
          <Button onClick={openRegister} className="bg-orange-500 text-white hover:bg-orange-600">
            <Plus className="mr-2 h-4 w-4" />
            Cadastrar jogos
          </Button>
        )}
      </PageHeader>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : lotteries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Nenhuma loteria disponível para o Banco de Jogos.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Abas por loteria */}
          <div className="-mx-1 flex gap-1 overflow-x-auto border-b px-1">
            {lotteries.map((lottery) => {
              const active = lottery.id === selected?.id;
              const hovered = hoveredLotteryId === lottery.id;
              const color = lottery.color || "#64748b";
              return (
                <button
                  key={lottery.id}
                  type="button"
                  onClick={() => selectLottery(lottery.id)}
                  onMouseEnter={() => setHoveredLotteryId(lottery.id)}
                  onMouseLeave={() => setHoveredLotteryId(null)}
                  onFocus={() => setHoveredLotteryId(lottery.id)}
                  onBlur={() => setHoveredLotteryId(null)}
                  className={cn(
                    "flex shrink-0 items-center gap-2 rounded-t-md border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                    active || hovered ? "text-foreground" : "border-transparent text-muted-foreground"
                  )}
                  style={{
                    borderColor: active || hovered ? color : "transparent",
                    // hover: fundo suave na cor da loteria (a aba ativa fica só com a linha)
                    backgroundColor: hovered && !active ? `${color}1F` : undefined,
                  }}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: lottery.color || "#64748b" }}
                  />
                  {lottery.name}
                  {lottery.open_sheets > 0 && (
                    <span
                      className="rounded-full bg-slate-200 px-1.5 text-[11px] font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                      title={`${lottery.open_sheets} ${lottery.open_sheets === 1 ? "planilha em andamento" : "planilhas em andamento"}`}
                    >
                      {lottery.open_sheets}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {selected && (
            <Card>
              <CardContent className="space-y-4 p-4 sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="space-y-2 sm:w-72">
                    <Label>Quantidade de dezenas</Label>
                    <Select value={dezenasFilter} onValueChange={setDezenasFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {selected.groups.map((group) => (
                          <SelectItem key={group.dezenas} value={String(group.dezenas)}>
                            {pad2(group.dezenas)} dezenas
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    className={appDialogSecondaryButton}
                    onClick={() => setDezenasFilter("all")}
                    disabled={dezenasFilter === "all"}
                  >
                    Limpar
                  </Button>
                </div>

                {groups.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                    {selected.groups.length === 0
                      ? `Nenhum jogo cadastrado em ${selected.name}.`
                      : "Nenhum grupo para essa quantidade de dezenas."}
                    {canManage && selected.groups.length === 0 && (
                      <div className="mt-3">
                        <Button onClick={openRegister} className="bg-orange-500 text-white hover:bg-orange-600">
                          <Plus className="mr-2 h-4 w-4" />
                          Cadastrar jogos
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border">
                    <div className="hidden grid-cols-[1fr_1fr_auto] gap-4 bg-muted/60 px-4 py-3 text-sm font-semibold sm:grid">
                      <span>Quantidade de dezenas</span>
                      <span>Jogos cadastrados</span>
                      <span className="w-32">Ação</span>
                    </div>
                    {groups.map((group) => (
                      <div
                        key={group.dezenas}
                        className="flex flex-col gap-2 border-t px-4 py-3 sm:grid sm:grid-cols-[1fr_1fr_auto] sm:items-center sm:gap-4"
                      >
                        <span className="flex items-center gap-3 font-medium">
                          <Layers className="h-4 w-4 text-muted-foreground" />
                          {pad2(group.dezenas)} dezenas
                        </span>
                        <span className="text-sm">
                          {group.games_count.toLocaleString("pt-BR")} {group.games_count === 1 ? "jogo" : "jogos"}
                        </span>
                        <Link
                          to={`/banco-de-boloes/${selected.id}/jogos/${group.dezenas}`}
                          className="flex w-32 items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                          <Eye className="h-4 w-4" />
                          Ver jogos
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  {groups.length} {groups.length === 1 ? "grupo encontrado" : "grupos encontrados"} ·{" "}
                  {selected.total_games.toLocaleString("pt-BR")} jogos no total em {selected.name}.
                </p>
              </CardContent>
            </Card>
          )}

          {selected && (
            <GameBankSheetsPanel key={selected.id} lottery={selected} canManage={canManage} onChanged={loadLotteries} />
          )}
        </>
      )}

      {/* Quantidade de dezenas do novo cadastro (define as colunas da planilha) */}
      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <AppDialogContent
          icon={Plus}
          title="Cadastrar jogos"
          description={selected ? `${selected.name}: quantas dezenas tem cada jogo?` : undefined}
          className="sm:max-w-md"
        >
          <AppDialogBody>
            <div className="space-y-2">
              <Label>Quantidade de dezenas por jogo</Label>
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                value={registerDezenas}
                disabled={!!selected?.rules.length}
                onChange={(e) => setRegisterDezenas(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && confirmRegister()}
                placeholder="Ex.: 20"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                A planilha terá uma coluna por dezena e é salva automaticamente. Os jogos só entram no banco quando
                ela é conferida e finalizada.
              </p>
            </div>
          </AppDialogBody>
          <AppDialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button className={appDialogSecondaryButton} onClick={() => setRegisterOpen(false)}>
              Cancelar
            </Button>
            <Button className={appDialogPrimaryButton} onClick={confirmRegister} disabled={!Number(registerDezenas)}>
              Abrir planilha
            </Button>
          </AppDialogFooter>
        </AppDialogContent>
      </Dialog>
    </Main>
  );
};
